import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, FormControlLabel, Grid, Switch, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { FC, useContext, useEffect } from 'react';
import { FincenReportContext } from 'hooks/FincenReportContext';
import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';

import { AddressInfo, ValidationState } from '../../../types';
import { AddressType, addressTypeOptions, TransferorType, TrusteePersonType } from '../../constants';
import AddressBlock from './AddressBlock';

const useStyles = makeStyles((theme: Theme) => ({
  formControlLabel: {
    marginLeft: 0,
    marginBottom: theme.spacing(3.5),
    '& .MuiTypography-root': {
      fontSize: pxToRem(14),
      color: theme.palette.text.primary,
    },
  },
  switch: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(-1.25),
  },
  addressContainer: {
    marginTop: theme.spacing(1),
  },
  box: {
    marginTop: theme.spacing(2),
  },
}));

type TransferorAddressUpdate = Omit<AddressInfo, 'address' | 'foreignEntity'>;

interface TransferorAddressBlockProps extends AddressInfo {
  transferorType: TransferorType | TrusteePersonType;
  validation: ValidationState;
  onAddressChange: (address: AddressFields) => void;
  onUpdate: (updates: TransferorAddressUpdate) => void;
  transferorIndex?: number;
  trusteeIndex?: number;
  addressTypeFieldName?: string;
  clearFieldError?: (fieldPath: string) => void;
}

const TransferorAddressBlock: FC<TransferorAddressBlockProps> = ({
  transferorType,
  address,
  foreignBusinessNoUSLocation,
  foreignBusinessAddress,
  foreignPrincipalAddress,
  primaryUSAddress,
  addressType,
  validation,
  foreignEntity,
  onAddressChange,
  onUpdate,
  transferorIndex = 0,
  trusteeIndex,
  addressTypeFieldName,
  clearFieldError,
}) => {
  const classes = useStyles();
  const fincenReportCtx = useContext(FincenReportContext);
  const { generateButtonClicked } = fincenReportCtx ?? {};

  const fieldName = addressTypeFieldName || `[${transferorIndex}].addressType`;

  // when foreignBusinessNoUSLocation is false and no addressType is set
  useEffect(() => {
    const isEntityType = transferorType === TransferorType.Entity || transferorType === TrusteePersonType.LegalEntity;
    if (isEntityType && !foreignBusinessNoUSLocation && !addressType) {
      onUpdate({
        addressType: AddressType.Foreign, // "Principal Place of Business (U.S.)"
        foreignBusinessAddress: foreignBusinessAddress,
        foreignPrincipalAddress: foreignPrincipalAddress,
        primaryUSAddress: primaryUSAddress,
      });
    }
  }, [transferorType, foreignBusinessNoUSLocation, addressType]);

  // Reusable method to build field prefix with trustee path when applicable
  const buildFieldPrefix = (fieldPath: string): string => {
    return trusteeIndex !== undefined
      ? `[${transferorIndex}].trustees[${trusteeIndex}].${fieldPath}`
      : `[${transferorIndex}].${fieldPath}`;
  };

  const getFieldError = (fieldName: string) => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;

    return validation.errors.find((error) => {
      // Match exact field name or field name with array index prefix (e.g., [0].addressType, [1].addressType)
      return (
        error.field === fieldName ||
        error.field.endsWith(`.${fieldName}`) ||
        error.field.match(new RegExp(`^\\[\\d+\\]\\.${fieldName}$`))
      );
    });
  };

  if (transferorType === TransferorType.Individual) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box>
            <Typography variant="h3">Residential Address</Typography>
          </Box>
          <AddressBlock
            address={address || {}}
            onChange={onAddressChange}
            validation={validation}
            isUSAddress={!foreignEntity}
            fieldPrefix={buildFieldPrefix('address')}
            clearFieldError={clearFieldError}
            isRequired={true}
            transferorIndex={transferorIndex}
            trusteeIndex={trusteeIndex}
          />
        </Grid>
      </Grid>
    );
  }

  // Transferor Trust - Hide all address sections
  if (transferorType === TransferorType.Trust) {
    return null;
  }

  if (transferorType === TransferorType.Entity || transferorType === TrusteePersonType.LegalEntity) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box my={2}>
            <Typography variant="h3">Business Address</Typography>
          </Box>
          <Grid item xs={6}>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <Switch
                  checked={foreignBusinessNoUSLocation || false}
                  onChange={(e) =>
                    onUpdate({
                      foreignBusinessNoUSLocation: e.target.checked,
                      // When toggling OFF, default to "Principal Place of Business (U.S.)"
                      // When toggling ON, clear addressType as it's not applicable
                      addressType: e.target.checked ? undefined : AddressType.Foreign,
                      foreignBusinessAddress: foreignBusinessAddress,
                      foreignPrincipalAddress: foreignPrincipalAddress,
                      primaryUSAddress: primaryUSAddress,
                    })
                  }
                  name="foreignBusinessNoUSLocation"
                  color="primary"
                  size="small"
                  className={classes.switch}
                  data-testid={`fincen-transferor-${transferorIndex}-foreign-business-no-us-location-switch`}
                />
              }
              label="Foreign principal place of business with no U.S. location"
              data-testid={`fincen-transferor-${transferorIndex}-foreign-business-no-us-location-label`}
            />
          </Grid>

          {foreignBusinessNoUSLocation ? (
            <div className={classes.addressContainer}>
              <Box className={classes.box}>
                <Typography variant="h3">Foreign Business Address</Typography>
              </Box>
              <AddressBlock
                address={foreignBusinessAddress || {}}
                onChange={(address) => {
                  onUpdate({
                    foreignBusinessAddress: address,
                  });
                }}
                validation={validation}
                isUSAddress={false}
                showStateLabel={true}
                fieldPrefix={buildFieldPrefix('foreignBusinessAddress')}
                clearFieldError={clearFieldError}
                isRequired={true}
                transferorIndex={transferorIndex}
                trusteeIndex={trusteeIndex}
              />
            </div>
          ) : (
            <>
              <Grid container>
                <Grid item xs={6}>
                  <AgentNetDropdownSelector
                    label="Address Type"
                    name={fieldName}
                    required
                    value={addressType || ''}
                    menuOption={(value: string) => {
                      onUpdate({
                        addressType: value as AddressType,
                      });
                      clearFieldError?.(fieldName);
                    }}
                    options={addressTypeOptions}
                    errs={validation.errors}
                    data-testid={`fincen-transferor-${transferorIndex}-address-type-select`}
                    showValidation={!!getFieldError('addressType')}
                    error={!!getFieldError('addressType')}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
              </Grid>

              {addressType === AddressType.Primary && (
                <>
                  <div className={classes.addressContainer}>
                    <Box className={classes.box}>
                      <Typography variant="h3">Foreign Principal Place of Business</Typography>
                    </Box>

                    <AddressBlock
                      address={foreignPrincipalAddress || {}}
                      onChange={(address) => {
                        onUpdate({
                          foreignPrincipalAddress: address,
                        });
                      }}
                      validation={validation}
                      isUSAddress={false}
                      showStateLabel={true}
                      fieldPrefix={buildFieldPrefix('foreignPrincipalAddress')}
                      clearFieldError={clearFieldError}
                      isRequired={true}
                      transferorIndex={transferorIndex}
                      trusteeIndex={trusteeIndex}
                    />
                  </div>

                  <div className={classes.addressContainer}>
                    <Box className={classes.box}>
                      <Typography variant="h3">Primary U.S. Location</Typography>
                    </Box>
                    <AddressBlock
                      address={primaryUSAddress || {}}
                      onChange={(address) => {
                        onUpdate({
                          primaryUSAddress: address,
                        });
                      }}
                      validation={validation}
                      isUSAddress={true}
                      showStateLabel={true}
                      fieldPrefix={buildFieldPrefix('primaryUSAddress')}
                      clearFieldError={clearFieldError}
                      isRequired={true}
                      transferorIndex={transferorIndex}
                      trusteeIndex={trusteeIndex}
                    />
                  </div>
                </>
              )}

              {addressType === AddressType.Foreign && (
                <div className={classes.addressContainer}>
                  <Box className={classes.box}>
                    <Typography variant="h3">Principal Place of Business (U.S.)</Typography>
                  </Box>
                  <AddressBlock
                    address={primaryUSAddress || {}}
                    onChange={(newAddress) => {
                      onUpdate({
                        primaryUSAddress: newAddress,
                      });
                    }}
                    validation={validation}
                    isUSAddress={true}
                    fieldPrefix={buildFieldPrefix('primaryUSAddress')}
                    clearFieldError={clearFieldError}
                    isRequired={true}
                    transferorIndex={transferorIndex}
                    trusteeIndex={trusteeIndex}
                  />
                </div>
              )}
            </>
          )}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid item xs={6}>
          <FormControlLabel
            className={classes.formControlLabel}
            control={
              <Switch
                checked={foreignBusinessNoUSLocation || false}
                onChange={(e) =>
                  onUpdate({
                    foreignBusinessNoUSLocation: e.target.checked,
                    // When toggling OFF, default to "Principal Place of Business (U.S.)"
                    // When toggling ON, clear addressType as it's not applicable
                    addressType: e.target.checked ? undefined : AddressType.Foreign,
                    foreignBusinessAddress: foreignBusinessAddress,
                    foreignPrincipalAddress: foreignPrincipalAddress,
                    primaryUSAddress: primaryUSAddress,
                  })
                }
                name="foreignBusinessNoUSLocation"
                color="primary"
                className={classes.switch}
                data-testid={
                  trusteeIndex !== undefined
                    ? `fincen-trustee-${transferorIndex}-${trusteeIndex}-foreign-business-no-us-location-switch`
                    : `fincen-transferor-${transferorIndex}-foreign-business-no-us-location-switch`
                }
              />
            }
            label="Foreign principal place of business with no U.S. location"
            data-testid={
              trusteeIndex !== undefined
                ? `fincen-trustee-${transferorIndex}-${trusteeIndex}-foreign-business-no-us-location-label`
                : `fincen-transferor-${transferorIndex}-foreign-business-no-us-location-label`
            }
          />
        </Grid>

        {foreignBusinessNoUSLocation ? (
          <div className={classes.addressContainer}>
            <AddressBlock
              address={foreignBusinessAddress || {}}
              onChange={(address) =>
                onUpdate({
                  foreignBusinessAddress: address,
                })
              }
              validation={validation}
              isUSAddress={false}
              showStateLabel={true}
              fieldPrefix={buildFieldPrefix('foreignBusinessAddress')}
              clearFieldError={clearFieldError}
              isRequired={true}
              transferorIndex={transferorIndex}
              trusteeIndex={trusteeIndex}
            />
          </div>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <AgentNetDropdownSelector
                  label="Address Type"
                  name={fieldName}
                  value={addressType || ''}
                  menuOption={(value: string) => {
                    onUpdate({
                      addressType: value as AddressType,
                    });
                    clearFieldError?.(fieldName);
                  }}
                  options={addressTypeOptions}
                  error={!!getFieldError('addressType')}
                  errs={validation.errors}
                  data-testid={
                    trusteeIndex !== undefined
                      ? `fincen-trustee-${transferorIndex}-${trusteeIndex}-address-type-select`
                      : `fincen-transferor-${transferorIndex}-address-type-select`
                  }
                  showValidation={!!getFieldError('addressType')}
                  variant="outlined"
                  required
                  fullWidth
                />
              </Grid>
            </Grid>

            {addressType === AddressType.Primary && (
              <>
                <div className={classes.addressContainer}>
                  <Box className={classes.box}>
                    <Typography variant="h3">Foreign Principal Place of Business</Typography>
                  </Box>
                  <AddressBlock
                    address={foreignPrincipalAddress || {}}
                    onChange={(address) =>
                      onUpdate({
                        foreignPrincipalAddress: address,
                      })
                    }
                    validation={validation}
                    isUSAddress={false}
                    showStateLabel={true}
                    fieldPrefix={buildFieldPrefix('foreignPrincipalAddress')}
                    clearFieldError={clearFieldError}
                    isRequired={true}
                    transferorIndex={transferorIndex}
                    trusteeIndex={trusteeIndex}
                  />
                </div>

                <div className={classes.addressContainer}>
                  <Box className={classes.box}>
                    <Typography variant="h3">Primary U.S. Location</Typography>
                  </Box>
                  <AddressBlock
                    address={primaryUSAddress || {}}
                    onChange={(newAddress) => {
                      onUpdate({
                        primaryUSAddress: newAddress,
                      });
                    }}
                    validation={validation}
                    isUSAddress={true}
                    fieldPrefix={buildFieldPrefix('primaryUSAddress')}
                    clearFieldError={clearFieldError}
                    isRequired={true}
                    transferorIndex={transferorIndex}
                    trusteeIndex={trusteeIndex}
                  />
                </div>
              </>
            )}

            {addressType === AddressType.Foreign && (
              <div className={classes.addressContainer}>
                <Box className={classes.box}>
                  <Typography variant="h3">Principal Place of Business (U.S.)</Typography>
                </Box>
                <AddressBlock
                  address={primaryUSAddress || {}}
                  onChange={(newAddress) => {
                    onUpdate({
                      primaryUSAddress: newAddress,
                    });
                  }}
                  validation={validation}
                  isUSAddress={true}
                  fieldPrefix={buildFieldPrefix('primaryUSAddress')}
                  clearFieldError={clearFieldError}
                  isRequired={true}
                  transferorIndex={transferorIndex}
                  trusteeIndex={trusteeIndex}
                />
              </div>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};
export default TransferorAddressBlock;
